<template>
  <div class="amortization-container" ref="containerRef">
    <div class="input-area">
      <div class="input-row single-row">
        <div class="input-group-section section-left">
          <div class="section-title">无形资产</div>
          <div class="input-line">
            <div class="input-group">
              <label class="input-label">摊销年限</label>
              <div class="input-field-container">
                <input
                  type="number"
                  v-model.number="
                    amortizationData.items['无形资产'].amortizationYears
                  "
                  class="input-field"
                  placeholder="请输入"
                  step="1"
                  min="1"
                  @change="recalculateAllData"
                />
                <span class="input-unit">年</span>
              </div>
            </div>
          </div>
        </div>
        <div class="input-group-section section-right">
          <div class="section-title">其他资产</div>
          <div class="input-line">
            <div class="input-group">
              <label class="input-label">摊销年限</label>
              <div class="input-field-container">
                <input
                  type="number"
                  v-model.number="
                    amortizationData.items['其他资产'].amortizationYears
                  "
                  class="input-field"
                  placeholder="请输入"
                  step="1"
                  min="1"
                  @change="recalculateAllData"
                />
                <span class="input-unit">年</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="table-wrapper">
      <div class="table-scroll-area">
        <table class="amortization-table">
          <thead>
            <tr>
              <th class="seq-col">序号</th>
              <th class="item-col">项 目</th>
              <th class="total-col">合计</th>
              <th class="year-col" v-for="year in displayYears" :key="year">
                {{ year }}
              </th>
            </tr>
            <tr>
              <th class="seq-col">-</th>
              <th class="item-col">建设期/运营期</th>
              <th class="total-col">-</th>
              <template v-for="(group, groupIndex) in yearSpanGroups">
                <th class="year-col period-type-col" :colspan="group.length">
                  <span v-if="group.type === 'construction'">建设期</span>
                  <span v-else>运营期</span>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <template v-for="(group, groupIndex) in assetTypeGroups">
              <tr class="group-header">
                <td class="seq-col">{{ groupIndex + 1 }}</td>
                <td class="item-col group-title">{{ group.type }}</td>
                <td class="total-col">-</td>
                <td
                  class="year-col"
                  v-for="(value, yearIndex) in group.originalValues"
                  :key="`header-${groupIndex}-${yearIndex}`"
                ></td>
              </tr>
              <tr class="detail-row">
                <td class="seq-col"></td>
                <td class="item-col sub-item">原值</td>
                <td class="total-col">
                  {{ formatNumber(group.originalTotal) }}
                </td>
                <td
                  class="year-col"
                  v-for="(value, yearIndex) in group.originalValues"
                  :key="`orig-${groupIndex}-${yearIndex}`"
                >
                  <span v-if="yearIndex < displayYears.length">{{
                    formatNumber(value)
                  }}</span>
                </td>
              </tr>
              <tr class="detail-row">
                <td class="seq-col"></td>
                <td class="item-col sub-item">摊销</td>
                <td class="total-col">
                  {{ formatNumber(group.amortizationTotal) }}
                </td>
                <td
                  class="year-col"
                  v-for="(value, yearIndex) in group.amortizationValues"
                  :key="`dep-${groupIndex}-${yearIndex}`"
                >
                  <span
                    v-if="
                      yearIndex < displayYears.length &&
                      yearIndex >= constructionPeriod - 1
                    "
                    >{{ formatNumber(value) }}</span
                  >
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr class="summary-row">
              <td class="seq-col"></td>
              <td class="item-col">原值合计</td>
              <td class="total-col">
                {{ formatNumber(totalOriginalValue) }}
              </td>
              <td
                class="year-col"
                v-for="(value, yearIndex) in totalOriginalValuesByYear"
                :key="`orig-total-${yearIndex}`"
              >
                <span v-if="yearIndex < displayYears.length">{{
                  formatNumber(value)
                }}</span>
              </td>
            </tr>
            <tr class="summary-row">
              <td class="seq-col"></td>
              <td class="item-col">摊销合计</td>
              <td class="total-col">
                {{ formatNumber(totalAmortizationValue) }}
              </td>
              <td
                class="year-col"
                v-for="(value, yearIndex) in totalAmortizationValuesByYear"
                :key="`dep-total-${yearIndex}`"
              >
                <span v-if="yearIndex < displayYears.length">{{
                  formatNumber(value)
                }}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { amortization } from "@/utils/comp.js";

export default {
  name: "Amortization",
  data() {
    return {
      debounceTimer: null,
    };
  },
  computed: {
    projectData() {
      return this.$store.state.projectData || {};
    },
    basicData() {
      return this.projectData["基础数据"] || {};
    },
    amortizationData() {
      // 修复：确保数据结构正确初始化
      const costData = this.projectData?.["成本费用"] || {};
      const data = costData["摊销"] || {};

      // 确保 items 对象存在
      if (!data.items) {
        data.items = {};
      }

      // 确保无形资产数据存在
      if (!data.items["无形资产"]) {
        data.items["无形资产"] = {
          originalValue: 0,
          amortizationYears: 10,
          amortizationTotal: 0,
          originalValues: Array(this.calculationPeriod).fill(0),
          amortizationValues: Array(this.calculationPeriod).fill(0),
        };
      }

      // 确保其他资产数据存在
      if (!data.items["其他资产"]) {
        data.items["其他资产"] = {
          originalValue: 0,
          amortizationYears: 5,
          amortizationTotal: 0,
          originalValues: Array(this.calculationPeriod).fill(0),
          amortizationValues: Array(this.calculationPeriod).fill(0),
        };
      }

      // 确保 total 对象存在
      if (!data.total) {
        data.total = {
          amortizationTotal: 0,
          originalValues: Array(this.calculationPeriod).fill(0),
          amortizationValues: Array(this.calculationPeriod).fill(0),
        };
      }

      return data;
    },
    calculationPeriod() {
      return this.basicData?.calculationPeriod || 12;
    },
    constructionPeriod() {
      return this.basicData?.constructionPeriod || 1;
    },
    operationPeriod() {
      return this.basicData?.operationPeriod || 11;
    },
    displayYears() {
      return Array.from({ length: this.calculationPeriod }, (_, i) => i + 1);
    },
    yearSpanGroups() {
      const groups = [];
      let currentType = null;
      let startIndex = 0;

      for (let i = 0; i < this.displayYears.length; i++) {
        const year = this.displayYears[i];
        const isConstruction = year <= this.constructionPeriod;
        const type = isConstruction ? "construction" : "operation";

        if (currentType === null) {
          currentType = type;
        } else if (type !== currentType) {
          groups.push({
            type: currentType,
            start: startIndex,
            length: i - startIndex,
          });
          currentType = type;
          startIndex = i;
        }
      }

      groups.push({
        type: currentType,
        start: startIndex,
        length: this.displayYears.length - startIndex,
      });

      return groups;
    },
    assetTypeGroups() {
      const groups = [];
      const assetTypes = [
        { key: "无形资产", name: "无形资产" },
        { key: "其他资产", name: "其他资产" },
      ];

      for (const assetType of assetTypes) {
        const data = this.amortizationData.items[assetType.key];
        if (!data) continue;

        groups.push({
          type: assetType.name,
          originalValue: data.originalValue,
          amortizationYears: data.amortizationYears,
          originalValues: data.originalValues,
          amortizationValues: data.amortizationValues,
          originalTotal: data.originalValue,
          amortizationTotal: data.amortizationTotal,
        });
      }

      return groups;
    },
    // 从vuex中获取总合计数据
    totalOriginalValue() {
      // 原值合计应为无形资产和其他资产原值之和
      return (
        (this.amortizationData.items["无形资产"]?.originalValue || 0) +
        (this.amortizationData.items["其他资产"]?.originalValue || 0)
      );
    },
    totalOriginalValuesByYear() {
      return (
        this.amortizationData.total?.originalValues ||
        Array(this.calculationPeriod).fill(0)
      );
    },
    totalAmortizationValuesByYear() {
      return (
        this.amortizationData.total?.amortizationValues ||
        Array(this.calculationPeriod).fill(0)
      );
    },
    totalAmortizationValue() {
      return this.amortizationData.total?.amortizationTotal || 0;
    },
  },
  watch: {
    "basicData.calculationPeriod": {
      handler() {
        this.handleDataChange();
      },
      deep: false,
    },
    "basicData.constructionPeriod": {
      handler() {
        this.handleDataChange();
      },
      deep: false,
    },
    "basicData.operationPeriod": {
      handler() {
        this.handleDataChange();
      },
      deep: false,
    },
  },
  mounted() {
    // 初始化时执行一次计算
    this.$nextTick(() => {
      this.handleDataChange();
    });
  },
  methods: {
    // 防抖处理函数
    recalculateAllData() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.handleDataChange();
      }, 300); // 300ms 防抖延迟
    },

    // 处理数据变化的主方法
    handleDataChange() {
      // 验证摊销年限是否有效
      const items = this.amortizationData.items;
      const minAmortizationYears = 1;

      // 确保摊销年限不小于最小值
      if (
        items["无形资产"] &&
        items["无形资产"].amortizationYears < minAmortizationYears
      ) {
        items["无形资产"].amortizationYears = minAmortizationYears;
      }

      if (
        items["其他资产"] &&
        items["其他资产"].amortizationYears < minAmortizationYears
      ) {
        items["其他资产"].amortizationYears = minAmortizationYears;
      }

      // 更新 store 中的数据
      this.$store.commit("SET_PROJECT_DATA", {
        ...this.$store.state.projectData,
        成本费用: {
          ...this.$store.state.projectData["成本费用"],
          摊销: this.amortizationData,
        },
      });

      // 执行摊销计算
      if (typeof amortization === "function") {
        amortization();
      }
    },

    formatNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return "";
      if (num === 0) return "";
      return Number(num)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
  },
  beforeDestroy() {
    // 清理定时器
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
};
</script>

<style scoped>
.amortization-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.input-area {
  padding: 15px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}

.input-row {
  display: flex;
  gap: 30px;
}

.input-group-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 15px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #fff;
  /* flex: 1; */
}

.section-left {
  margin-right: 15px;
}

.section-right {
  margin-left: 15px;
}

.input-line {
  display: flex;
  gap: 20px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  padding-bottom: 5px;
  border-bottom: 2px solid #409eff;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.input-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.input-field {
  width: 80px;
  padding: 6px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  text-align: right;
}

.input-field:focus {
  border-color: #409eff;
}

.input-unit {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.table-wrapper {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table-scroll-area {
  flex: 1;
  overflow: auto;
}

.amortization-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
}

.amortization-table th,
.amortization-table td {
  text-align: center;
  border: 1px solid #dfe6ec;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.amortization-table thead th {
  background: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

.seq-col {
  width: 50px;
  min-width: 50px;
}

.item-col {
  width: 200px;
  min-width: 200px;
  text-align: left;
}

.total-col {
  width: 100px;
  min-width: 100px;
  font-weight: 600;
}

.year-col {
  padding: 4px 2px;
}

.period-type-col {
  background: #e8f4f8;
}

.group-header {
  background: #e8f0f8;
}

.group-header:hover {
  background: #dce6f0;
}

.group-title {
  font-weight: 600;
  color: #303133;
}

.sub-item {
  padding-left: 30px;
  color: #606266;
}

.detail-row:hover {
  background: #f5f7fa;
}

.summary-row {
  background: #f0f9eb;
  font-weight: 600;
}

.summary-row:hover {
  background: #e1f3d8;
}

.input-field-container {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
